(() => {
  const storageKey = "jpi-impact-latest-v2";
  const limits = {
    field_applications: 100000000,
    acres: 1000000000,
    countries: 250,
    customers: 100000000,
    trees_planted: 100000000,
    co2_pounds: 100000000000,
    merit_grant_giveaway_value: 1000000000,
    merit_grant_raised: 1000000000,
    merit_grant_goal: 1000000000,
  };

  const parseCsvRow = (row) => {
    const cells = [];
    let cell = "";
    let quoted = false;
    for (let index = 0; index < row.length; index += 1) {
      const character = row[index];
      if (character === '"') {
        if (quoted && row[index + 1] === '"') { cell += '"'; index += 1; }
        else quoted = !quoted;
      } else if (character === "," && !quoted) {
        cells.push(cell.trim());
        cell = "";
      } else cell += character;
    }
    cells.push(cell.trim());
    return cells;
  };

  const parseCsv = (raw) => {
    const lines = raw.replace(/^\uFEFF/, "").split(/\r?\n/).filter(Boolean);
    if (lines.length < 2) throw new Error("Impact feed is missing its data row");
    const headers = parseCsvRow(lines[0]);
    const values = parseCsvRow(lines[1]);
    return Object.fromEntries(headers.map((header, index) => [header, values[index]]));
  };

  const safeNumber = (raw, key) => {
    const value = Number(String(raw ?? "").replace(/[,$]/g, ""));
    if (!Number.isFinite(value) || value < 0 || value > limits[key]) throw new Error("Impact value outside its approved range");
    return Math.round(value);
  };

  const normalize = (raw, fallbackGoal, defaults = {}) => {
    const lastUpdated = String(raw.last_updated ?? "").trim();
    if (lastUpdated && !/^\d{4}-\d{2}-\d{2}$/.test(lastUpdated)) throw new Error("Impact date must use YYYY-MM-DD");
    const optionalNumber = (key, fallback = 0) => {
      const value = raw[key];
      return value == null || String(value).trim() === "" ? fallback : safeNumber(value, key);
    };
    return {
      field_applications: safeNumber(raw.field_applications, "field_applications"),
      acres: safeNumber(raw.acres, "acres"),
      countries: safeNumber(raw.countries, "countries"),
      customers: safeNumber(raw.customers, "customers"),
      trees_planted: safeNumber(raw.trees_planted, "trees_planted"),
      co2_pounds: safeNumber(raw.co2_pounds, "co2_pounds"),
      merit_grant_giveaway_value: optionalNumber("merit_grant_giveaway_value", defaults.merit_grant_giveaway_value || 0),
      merit_grant_raised: safeNumber(raw.merit_grant_raised, "merit_grant_raised"),
      merit_grant_goal: safeNumber(raw.merit_grant_goal || fallbackGoal, "merit_grant_goal") || fallbackGoal,
      last_updated: lastUpdated,
    };
  };

  const format = (value, currency = false) => value > 0
    ? new Intl.NumberFormat("en-US", {
        maximumFractionDigits: 0,
        ...(currency ? { style: "currency", currency: "USD" } : {}),
      }).format(value)
    : "—";

  const animate = (element, target, currency) => {
    if (target <= 0) { element.textContent = "—"; return; }
    const suffix = element.getAttribute("data-jpi-suffix") || "";
    const display = (value) => `${format(value, currency)}${suffix}`;
    element.setAttribute("aria-label", display(target));
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      element.textContent = display(target);
      return;
    }
    const start = Math.max(0, target - 13);
    const slowStart = Math.max(start, target - 10);
    let current = start;
    element.textContent = display(current);
    const begin = () => {
      const fast = window.setInterval(() => {
        current += 1;
        element.textContent = display(Math.min(current, slowStart));
        if (current >= slowStart) {
          window.clearInterval(fast);
          const slow = window.setInterval(() => {
            current += 1;
            element.textContent = display(Math.min(current, target));
            if (current >= target) window.clearInterval(slow);
          }, 140);
        }
      }, 55);
    };
    if (!("IntersectionObserver" in window)) { begin(); return; }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { begin(); observer.disconnect(); }
    }, { threshold: 0.35 });
    observer.observe(element);
  };

  const render = (root, data) => {
    root.querySelectorAll("[data-jpi-counter]").forEach((element) => {
      const key = element.getAttribute("data-jpi-counter");
      animate(element, data[key] || 0, element.hasAttribute("data-jpi-currency"));
    });
    const progress = data.merit_grant_goal > 0
      ? Math.min(100, Math.max(0, data.merit_grant_raised / data.merit_grant_goal * 100))
      : 0;
    const label = root.querySelector("[data-jpi-progress-label]");
    const bar = root.querySelector("[data-jpi-progress-bar]");
    const track = root.querySelector("[data-jpi-progress-track]");
    const goal = root.querySelector("[data-jpi-goal]");
    const updated = root.querySelector("[data-jpi-last-updated]");
    if (label) label.textContent = `${Math.round(progress)}% funded`;
    if (bar) bar.style.width = `${progress}%`;
    if (track) track.setAttribute("aria-label", `${Math.round(progress)} percent of the Merit Grant goal raised`);
    if (goal) goal.textContent = format(data.merit_grant_goal, true);
    if (updated && data.last_updated) {
      updated.textContent = new Intl.DateTimeFormat("en-US", {
        month: "long", day: "numeric", year: "numeric", timeZone: "UTC",
      }).format(new Date(`${data.last_updated}T12:00:00Z`));
    }
  };

  const init = (root) => {
    if (!root || root.dataset.jpiReady === "true") return;
    root.dataset.jpiReady = "true";
    const fallbackGoal = Number(root.dataset.goal) || 30000;
    const fallback = {
      field_applications: 48367, acres: 48367, countries: 40, customers: 9024,
      trees_planted: 3198, co2_pounds: 327827,
      merit_grant_giveaway_value: 12136.25,
      merit_grant_raised: 12820, merit_grant_goal: fallbackGoal, last_updated: "2026-08-11",
    };
    const giveawayCounter = root.querySelector('[data-jpi-counter="merit_grant_giveaway_value"]');
    if (giveawayCounter) {
      giveawayCounter.textContent = format(fallback.merit_grant_giveaway_value, true);
      giveawayCounter.setAttribute("aria-label", format(fallback.merit_grant_giveaway_value, true));
    }
    try {
      const stored = JSON.parse(window.localStorage.getItem(storageKey) || "null");
      render(root, stored ? normalize(stored, fallbackGoal, fallback) : fallback);
    } catch { render(root, fallback); }
    if (giveawayCounter && giveawayCounter.textContent.trim() === "—") {
      giveawayCounter.textContent = format(fallback.merit_grant_giveaway_value, true);
    }

    const formFrame = root.querySelector(".jpi-form-shell iframe");
    root.querySelectorAll(".jpi-gift-card[data-jpi-amount]").forEach((card) => {
      card.addEventListener("click", (event) => {
        if (!formFrame) return;
        event.preventDefault();
        const amount = card.getAttribute("data-jpi-amount");
        const formUrl = new URL(formFrame.src);
        formUrl.searchParams.set("amount", amount);
        formFrame.src = formUrl.toString();
        root.querySelector("#jpi-give")?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });

    const feedUrl = root.dataset.impactFeed;
    if (!feedUrl) return;
    const refresh = async () => {
      try {
        const response = await fetch(feedUrl, { headers: { accept: "text/csv, application/json" }, cache: "no-store" });
        if (!response.ok) return;
        const rawText = await response.text();
        const raw = rawText.trim().startsWith("{") ? JSON.parse(rawText) : parseCsv(rawText);
        const data = normalize(raw, fallbackGoal, fallback);
        window.localStorage.setItem(storageKey, JSON.stringify(data));
        render(root, data);
        if (giveawayCounter && giveawayCounter.textContent.trim() === "—") {
          giveawayCounter.textContent = format(fallback.merit_grant_giveaway_value, true);
        }
      } catch { /* Retain the most recent available totals. */ }
    };
    refresh();
    window.setInterval(refresh, 5 * 60 * 1000);
  };

  const initAll = (container = document) => container
    .querySelectorAll("[data-jpi-donation-page]")
    .forEach(init);

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => initAll());
  else initAll();
  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
})();
